import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import i18n from "@/i18n";

dayjs.extend(relativeTime);

export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return value.toLocaleString(i18n.language, options);
}

/**
 * Locale-aware percentage from a ratio (0.95 → "95 %" / "95%"). The percent
 * style multiplies by 100 and handles the German space-before-% itself, so
 * pass the raw ratio, never a pre-multiplied value.
 */
export function formatPercent(ratio: number, fractionDigits = 0): string {
  return new Intl.NumberFormat(i18n.language, {
    style: "percent",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(ratio);
}

/** Locale-aware currency ("1.234,50 €" / "€1,234.50"). */
export function formatCurrency(
  value: number,
  currency: string,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(i18n.language, {
    style: "currency",
    currency,
    ...options,
  }).format(value);
}

/** API dates are calendar dates: "YYYY-MM-DD" parses to local midnight, as the
 *  charts' dayjs time adapter reads it. `new Date("YYYY-MM-DD")` is UTC midnight
 *  and lands a day early west of UTC. */
export function parseDate(value: Date | string | number): Date {
  return dayjs(value).toDate();
}

export function formatDate(
  value: Date | string | number,
  options?: Intl.DateTimeFormatOptions,
): string {
  return parseDate(value).toLocaleDateString(i18n.language, options);
}

/**
 * Locale-aware relative time, e.g. "vor 3 Stunden" / "3 hours ago".
 * Always suffixed. Keyed off the user's profile language via i18n, so it
 * matches the rest of the UI instead of the dayjs global locale.
 */
export function formatRelativeTime(value: Date | string | number): string {
  return dayjs(value).locale(i18n.language).fromNow();
}
